import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { createPortal } from "react-dom";
import { getBlockName } from "@circle-react-shared/BuilderUI/Helpers/getBlockName";
import { useSelectedBlock } from "@circle-react-shared/BuilderUI/Hooks/useBuilderStore";
import { Typography } from "@circle-react-uikit/Typography";
import { BLOCK_ACTIONS_Z_INDEX } from "./constants";
import { useCanvasToolsPositions } from "./useCanvasToolsPositions";

export interface CanvasBlockNameProps {
  "data-testid"?: string;
}

const TOP_OFFSET = -30; // Match with hover spacing
const NARROW_BLOCK_ADDITIONAL_OFFSET = -38; // Additional offset for narrow blocks
const NARROW_BLOCK_THRESHOLD = 110 + 64 + 8; // Block actions width + name width + some spacing

export const CanvasBlockName = ({
  "data-testid": dataTestId = "canvas-block-name",
}: CanvasBlockNameProps) => {
  const { meta } = useSelectedBlock();
  const [position, setPosition] = useState<CSSProperties>({});

  const { positions } = useCanvasToolsPositions();

  const blockName = meta ? getBlockName(meta) : "";

  useEffect(() => {
    if (!positions) {
      return;
    }

    // Calculate block width using elementRect
    const blockWidth = positions.elementRect.width;

    // Apply additional offset if block is narrow
    const topOffset =
      blockWidth < NARROW_BLOCK_THRESHOLD
        ? TOP_OFFSET + NARROW_BLOCK_ADDITIONAL_OFFSET
        : TOP_OFFSET;

    setPosition({
      position: "absolute",
      top: positions.elementRect.top + topOffset,
      left:
        blockWidth < NARROW_BLOCK_THRESHOLD
          ? positions.elementRect.right
          : positions.elementRect.left,
      transform:
        blockWidth < NARROW_BLOCK_THRESHOLD ? "translateX(-100%)" : undefined,
      zIndex: BLOCK_ACTIONS_Z_INDEX - 1, // Block actions has priority over block name
      pointerEvents: "auto",
    });
  }, [positions]);

  if (!blockName || position.top === undefined) {
    return null;
  }

  const blockNameElement = (
    <div
      className="bg-circle pointer-events-auto flex items-center rounded px-3 py-1 uppercase"
      style={position}
      data-testid={dataTestId}
    >
      <Typography.LabelXxs
        weight="bold"
        color="text-white"
        leading="leading-4"
        tracking="tracking-wide"
      >
        {blockName}
      </Typography.LabelXxs>
    </div>
  );

  // Find the CanvasCard container to portal to (this has the right coordinate system)
  const canvasCard = document.getElementById("builder-ui-canvas-card");
  if (!canvasCard) {
    return blockNameElement;
  }

  return createPortal(blockNameElement, canvasCard);
};
